import { FormEvent, useState } from "react";
import { postReq } from "../../utils/request";
import { useNavigate } from "react-router-dom";
import { setJwtToken } from "../../utils/token";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Cookies from 'js-cookie';
import { validatePassword } from "../../utils/passwordHelper";
import Button from "../../components/Button";
import Input from "../../components/Input";
import auth from "./auth.module.scss";
const ForgotPassword = () => {
  const navigate = useNavigate();
  // The form's fields; `email` below is the address the code was sent to.
  const [data, setData] = useState({ email: "", verificationCode: "", newPassword: "", confirmNewPassword: "" });
  const setField = (name: string, value: unknown) => setData((prev) => ({ ...prev, [name]: String(value ?? "") }));

  const [email, setEmail] = useState("");
  const [verificationFailed, setVerificationFailed] = useState("");
  const [showNewPasswordFields, setShowNewPasswordFields] = useState(false);
  const [emailError, setEmailError] = useState("");

  const onForgotPasswordSubmit = async () => {
    setEmailError("");
    if(!data.email){
        setEmailError("Email is required");
        return;
    }
    if (!emailIsValid(data.email)) {
        setEmailError("Invalid Email format");
        return;
    }
  
    setEmail(data.email);

    // Implement logic to send 2FA code to the user's email
    const resp = await postReq("users/forgot-password", {
      email: data.email,
    });

    if (resp.status === 200) {
      // The server always answers 200 with a generic message so accounts
      // cannot be enumerated — show the verification fields either way.
      setShowNewPasswordFields(true);
      const verificationToken = resp.body.accessToken;
      setJwtToken(verificationToken);
      toast.success(resp.body?.message || "If the email exists, a reset link has been sent", {
        autoClose: 5000, // 5 seconds
        className: "toast-success-message",
      });
    }
    else {
      setVerificationFailed(resp.body.message);
    }
  };
  const emailIsValid = (email: string): boolean => {
    const emailValid = email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);
    return !!emailValid;
  };
  const onResetPasswordSubmit = async () => {
    const passwordValidationMessage = validatePassword(data.newPassword);
    if (passwordValidationMessage) {
      toast.error(passwordValidationMessage);
      return;
    }
    if (data.newPassword !== data.confirmNewPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }
    const resp = await postReq("users/reset-password", {
      email,
      verificationCode: data.verificationCode,
      newPassword: data.newPassword,
      confirmNewPassword: data.confirmNewPassword,
    });
    if (resp.status === 200) {
      setJwtToken("");
      Cookies.remove('refreshToken');
      toast.success("Password changed successfully! Redirecting to login...", {
        autoClose: 5000, // 5 seconds
        className: "toast-success-message",
        onClose: () => navigate("/login"),
      });
    } else {
      setVerificationFailed(resp.body.message);
    }
    if(resp.status === 403)
      {
        setVerificationFailed(resp.body.message);
        toast.error(resp.body.message, {
          autoClose: 5000, // 5 seconds
          className: "toast-success-message",
          onClose: () => navigate("/login"),
        });
        
      }
  };

  return (
    <div className={`container`}>
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="d-flex justify-content-between align-items-center">
            <span className="componentTitle">Forgot password</span>
          </div>
          <form
            className={auth.authForm}
            onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              if (showNewPasswordFields) void onResetPasswordSubmit();
              else void onForgotPasswordSubmit();
            }}
          >
            <div className={auth.authField}>
              <Input
                type="email"
                id="email"
                label="Email address"
                value={data.email}
                onChange={setField}
                disabled={showNewPasswordFields}
                nativeProps={{
                  name: "email",
                  autoFocus: true,
                  autoComplete: "username",
                  inputMode: "email",
                  autoCapitalize: "none",
                  spellCheck: false,
                }}
              />
              {emailError ? (
                  <span className="errorClass">{emailError}</span>
                ) : null}
            </div>

            {showNewPasswordFields && (
              <>
                <div className={auth.authField}>
                  <Input
                    type="text"
                    id="verificationCode"
                    label="Verification code"
                    className={auth.codeField}
                    value={data.verificationCode}
                    onChange={setField}
                    nativeProps={{ name: "verificationCode", inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6 }}
                  />
                </div>

                <div className={auth.authField}>
                  <Input
                    type="password"
                    id="newPassword"
                    label="New password"
                    value={data.newPassword}
                    onChange={setField}
                    trimOnBlur={false}
                    nativeProps={{ name: "newPassword", autoComplete: "new-password" }}
                  />
                </div>

                <div className={auth.authField}>
                  <Input
                    type="password"
                    id="confirmNewPassword"
                    label="Confirm new password"
                    value={data.confirmNewPassword}
                    onChange={setField}
                    trimOnBlur={false}
                    nativeProps={{ name: "confirmNewPassword", autoComplete: "new-password" }}
                  />
                </div>
              </>
            )}

            {verificationFailed && (
              <span className="errorClass">{verificationFailed}</span>
            )}

            <div className={auth.authActions}>
              <Button type="submit" variant="primary" size="lg" title={showNewPasswordFields ? "Set the new password" : "Email me a verification code"}>
                {showNewPasswordFields ? "Set new password" : "Send reset code"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
